import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Search as SearchIcon,
    GetApp as DownloadIcon,
    FilterList as FilterIcon,
    Sort as SortIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { CustomPagination } from '../../../Tables/Pagination';
import { usePagination } from '../../../../hook/usePagination';
import _ from 'lodash';
import { ICampaignSales, IContestSales, TCampSortOrder } from '../../../../redux/contest/types';
import { CustomDatePicker } from '../../../DatePicker/CustomDatePicker';
import { ColumnsData, Table } from '../../../Tables/Table';
import { ThspianAvatar } from '../../../Avatar/ThspianAvatar';
import { formatDate, useDispatchPromise } from '../../../../utils/AppUtils';
import { BrandContestSalesList } from './BrandContestSalesList';
import { getContestManage } from '../../../../redux/contest/actions';
import { isEmpty } from '../../../../utils/lodash';
import {
    brandContestFilterControlSx,
    brandContestFilterSearchSx,
    brandContestFilterSelectSx,
    brandContestSecondaryIconButtonSx,
    brandContestTableCellTextSx,
    brandContestTableShellSx,
    editorialSelectMenuProps,
    midnight,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';

interface ContestSalesTabProps {
    sales?: IContestSales;
    onExportCSV: (sales?: ICampaignSales[]) => void;
    loading?: boolean;
    contestId?: string;
    /** Brand contest manage drawer — pill filters + luxe table chrome. */
    visualVariant?: 'default' | 'brand';
    /** Parent already loaded sales via manage endpoint — skip duplicate refetch on mount. */
    useParentManageData?: boolean;
}

/** Add functionaloity here to clear the filters */

export const ContestSalesTab: React.FC<ContestSalesTabProps> = ({
    sales,
    onExportCSV,
    contestId,
    visualVariant = 'default',
    useParentManageData = false,
}) => {
    const { themeStyle } = useThemeContext();
    const isBrand = visualVariant === 'brand';
    const [showFilters, setShowFilters] = useState(false);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [sortOrder, setSortOrder] = useState<TCampSortOrder | undefined>('asc');
    const [searchValue, setSearchValue] = useState('');
    const [date, setDate] = useState({});

    const dispatchPromise = useDispatchPromise();

    // Pagination for sales data
    const {
        pagination: salePagination,
        setPagination: setSalePagination,
        handleRowsChange: handleSaleRowsChange,
        handlePageChange: handleSalePageChange,
    } = usePagination(1, 10);

    const handleGetSalesData = useCallback(async () => {
        if (!contestId || useParentManageData) return;

        try {
            const response: any = await dispatchPromise(
                getContestManage({
                    contestId: contestId,
                    category: 'sales',
                    searchTerm: debouncedSearchValue,
                    sortOrder,
                    salePage: salePagination.page,
                    saleLimit: salePagination.rows,
                    ...(!isEmpty(date) && { ...date }),
                }),
            );

            if (response?.data?.sales?.pagination) {
                const { currentPage, pageSize, totalCount } = response.data.sales.pagination;
                setSalePagination({
                    page: currentPage,
                    rows: pageSize,
                    totalCount,
                });
            }
        } catch {
            /* saga + globalErrorHandler surface failures */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sortOrder,
        salePagination.page,
        salePagination.rows,
        debouncedSearchValue,
        contestId,
        date,
        useParentManageData,
        dispatchPromise,
    ]);

    useEffect(() => {
        handleGetSalesData();
    }, [handleGetSalesData]);

    // Create debounced search handler
    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            setDebouncedSearchValue(searchValue);
        }, 500),
    ).current;

    // Debounce and update debouncedSearchValue
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchValue]);

    const renderCustomImage = (params: ICampaignSales) => {
        return <ThspianAvatar user={params.created_by} />;
    };
    const cellTextSx = isBrand
        ? brandContestTableCellTextSx
        : { fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: theme[themeStyle].tag_white };

    const renderCustomTitle = (params: ICampaignSales) => {
        return (
            <Typography
                sx={{
                    ...cellTextSx,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                }}>
                @{params.created_by.userName}
            </Typography>
        );
    };

    const renderCustomDiscount = (params: ICampaignSales) => {
        return (
            <Typography
                sx={{
                    ...cellTextSx,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                }}>
                -{params.is_discounted ? (params.usd_discounted_price / params.usd_price) * 100 : '-'}%
            </Typography>
        );
    };

    const renderCustomCompleteStatus = (params: ICampaignSales) => {
        return (
            <Typography
                sx={{
                    ...cellTextSx,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                }}>
                {params.completed ? 'Completed' : 'Pending'}
            </Typography>
        );
    };

    const columns: ColumnsData[] = [
        {
            field: 'created_by',
            label: '',
            type: 'custom',
            customCell: renderCustomImage,
            sortable: false,
            width: 100,
        },
        {
            field: '',
            label: 'User',
            type: 'custom',
            sortable: true,
            width: 150,
            customCell: renderCustomTitle,
        },
        {
            field: 'invoice.formatted_amount_captured',
            label: 'Amount',
            type: 'text',
            sortable: true,
            width: 200,
        },

        {
            field: 'transaction_currency',
            label: 'Currency',
            type: 'text',
            sortable: true,
            width: 100,
        },
        {
            field: 'is_discounted',
            label: 'Discount',
            type: 'custom',
            sortable: true,
            width: 150,
            customCell: renderCustomDiscount,
        },
        {
            field: 'completed',
            label: 'Status',
            type: 'custom',
            sortable: true,
            width: 100,
            customCell: renderCustomCompleteStatus,
        },
        {
            field: 'createdAt',
            label: 'Created At',
            type: 'text',
            sortable: true,
            width: '100%',
            flex: 1,
        },
    ];

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setSearchValue(newValue);
        debouncedSearchHandler(newValue);
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setDebouncedSearchValue('');
    };

    const handleSortOrderChange = (event: SelectChangeEvent) => {
        setSortOrder(event.target.value as TCampSortOrder);
    };

    const handleExportCSV = () => {
        onExportCSV(sales?.data);
    };

    const handleSetDate = (field: string, value: string) => {
        setDate(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    return (
        <Box sx={{ width: '100%' }}>
            {!isBrand ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.2rem',
                        }}>
                        Sales
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportCSV}
                        sx={{
                            backgroundColor: theme[themeStyle].main_blue,
                            color: theme[themeStyle].primary_white,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].main_blue,
                                opacity: 0.8,
                            },
                        }}>
                        Export CSV
                    </Button>
                </Box>
            ) : null}

            {isBrand ? (
                <Box sx={{ mb: 2, width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1.5,
                            width: '100%',
                            gap: 1,
                        }}>
                        <Typography
                            sx={{
                                color: midnight.onSurface,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '1rem',
                                letterSpacing: '-0.01em',
                            }}>
                            Search Transactions
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                            <Tooltip title="Export CSV">
                                <IconButton
                                    aria-label="Export CSV"
                                    onClick={handleExportCSV}
                                    size="small"
                                    sx={brandContestSecondaryIconButtonSx}>
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showFilters ? 'Hide filters' : 'Show filters'}>
                                <IconButton
                                    aria-label="Toggle filters"
                                    onClick={() => setShowFilters(!showFilters)}
                                    size="small"
                                    sx={{
                                        ...brandContestSecondaryIconButtonSx,
                                        ...(showFilters
                                            ? { bgcolor: 'rgba(255,201,101,0.12)', borderColor: midnight.primary }
                                            : {}),
                                    }}>
                                    <FilterIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <TextField
                        placeholder="Search transactions"
                        value={searchValue}
                        onChange={handleSearchChange}
                        fullWidth
                        sx={{ width: '100%', ...brandContestFilterSearchSx }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: midnight.onSurfaceVariant, fontSize: 22 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue ? (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClearSearch} size="small" aria-label="Clear search">
                                        <CloseIcon sx={{ color: midnight.onSurfaceVariant, fontSize: 20 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    {showFilters && (
                        <Box
                            sx={{
                                mt: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                width: '100%',
                            }}>
                            <FormControl sx={brandContestFilterControlSx}>
                                <Select
                                    displayEmpty
                                    value={sortOrder ?? 'asc'}
                                    onChange={handleSortOrderChange}
                                    inputProps={{ 'aria-label': 'Sort order' }}
                                    MenuProps={editorialSelectMenuProps}
                                    renderValue={selected =>
                                        `Sort: ${selected === 'desc' ? 'Descending' : 'Ascending'}`
                                    }
                                    sx={brandContestFilterSelectSx}>
                                    <MenuItem value="asc">Ascending</MenuItem>
                                    <MenuItem value="desc">Descending</MenuItem>
                                </Select>
                            </FormControl>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                    width: '100%',
                                }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CustomDatePicker
                                        label="Start Date"
                                        brandLuxe={isBrand}
                                        onChange={e => {
                                            handleSetDate('startDate', e);
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CustomDatePicker
                                        label="End Date"
                                        brandLuxe={isBrand}
                                        onChange={e => {
                                            handleSetDate('endDate', e);
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            ) : (
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        fullWidth
                        placeholder="Search transactions by user..."
                        value={searchValue}
                        onChange={handleSearchChange}
                        InputLabelProps={{
                            sx: {
                                color: theme[themeStyle].tag_white,
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: theme[themeStyle].tag_white }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue && (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClearSearch} size="small">
                                        <CloseIcon sx={{ color: theme[themeStyle].tag_white }} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {
                                backgroundColor: theme[themeStyle].secondaryBg,
                                borderRadius: 2,
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme[themeStyle].placeholderGray50,
                                },
                            },
                        }}
                        sx={{
                            borderRadius: 1,
                            '& .MuiDataGrid-root': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                            '& .MuiDataGrid-filler': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterIcon />}
                        onClick={() => setShowFilters(!showFilters)}
                        sx={{
                            borderColor: theme[themeStyle].tag_white,
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                borderColor: theme[themeStyle].tag_white,
                                opacity: 0.8,
                            },
                        }}>
                        Filters
                    </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <InputLabel id="sort-order-label" sx={{ color: theme[themeStyle].tag_white }}>
                            Sort Order
                        </InputLabel>
                        <Select
                            labelId="sort-order-label"
                            value={sortOrder}
                            label="Sort Order"
                            onChange={handleSortOrderChange}
                            startAdornment={
                                <InputAdornment position="start">
                                    <SortIcon sx={{ color: theme[themeStyle].tag_white }} />
                                </InputAdornment>
                            }
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        marginTop: 0,
                                        '& .MuiMenuItem-root': {
                                            padding: 2,
                                        },
                                    },
                                },
                            }}
                            sx={{
                                backgroundColor: theme[themeStyle].table_header,
                                color: theme[themeStyle].tag_white,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme[themeStyle].primary_white,
                                },
                            }}>
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            )}

            {showFilters && !isBrand && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <CustomDatePicker
                            label="Start Date"
                            brandLuxe={isBrand}
                            onChange={e => {
                                handleSetDate('startDate', e);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <CustomDatePicker
                            label="End Date"
                            brandLuxe={isBrand}
                            onChange={e => {
                                handleSetDate('endDate', e);
                            }}
                        />
                    </Grid>
                </Grid>
            )}

            {isBrand ? (
                <Box sx={brandContestTableShellSx}>
                    <BrandContestSalesList
                        sales={sales?.data ?? []}
                        formatDateDay={iso => formatDate(iso, 'MMM D, YY')}
                        formatDateTime={iso => formatDate(iso, 'h:mm A')}
                    />
                </Box>
            ) : (
                <Box>
                    <Table
                        columns={columns}
                        tableType="sales"
                        staticData={sales?.data || []}
                        rowsPerPageOptions={[5, 10, 20]}
                        sx={{
                            backgroundColor: theme[themeStyle].table_header,
                            borderRadius: 2,
                        }}
                    />
                </Box>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: isBrand ? 'center' : 'flex-end', width: '100%' }}>
                <CustomPagination
                    variant={isBrand ? 'brand' : 'default'}
                    pagination={salePagination}
                    onPageChange={handleSalePageChange}
                    onRowsChange={handleSaleRowsChange}
                    rowsPerPageOptions={[5, 10, 20]}
                />
            </Box>
        </Box>
    );
};
